// Take a server out of rotation gracefully.
//
// This replaced ClearGhostsButton. That button released slots leaked by joins
// that never landed — capacity the server was refusing players over. The leak is
// gone: a server that reports its roster rewrites it every ~30s and the sync
// deletes anything it did not report, so there is nothing left to clean up, and
// nothing left for staff to interpret before deciding whether to press it.
//
// Drain is the action that was missing. Restart ends every match on the server
// instantly — players mid-match are disconnected, and anyone who paid a spawn
// fee for a match that no longer exists has to be refunded, which is why Restart
// carries a refund step. A drain stops new joins, lets the current players
// finish, then the server exits on its own.
//
// Button and confirmation are separate components so the caller can put the
// button in a tight row and the confirmation somewhere with room. A ConfirmBar
// dropped into a `flex-nowrap` row or a table cell cannot wrap and pushes the
// page sideways.

import { faArrowRightFromBracket } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useState } from 'react'
import { ConfirmBar } from '~/team_tools/components/ConfirmBar'
import { useNotification } from '~/team_tools/components/NotificationProvider'
import { drainServer } from './api'

export const DrainButton = ({ onAsk, disabled = false }: { onAsk: () => void; disabled?: boolean }) => (
    <button
        onClick={onAsk}
        disabled={disabled}
        title='Stop new joins and let the players on this server finish, then it exits'
        className='flex flex-row items-center gap-1 rounded border border-white/20 px-2 py-1 text-[10px] uppercase tracking-wide opacity-70 hover:bg-white/10 hover:opacity-100 disabled:pointer-events-none disabled:opacity-30'
    >
        <FontAwesomeIcon icon={faArrowRightFromBracket} />
        Drain
    </button>
)

/** Drain a server and say how it went. Shared by the one-click card button and
 *  the confirmation below, so both report the same way. */
export const useDrain = () => {
    const { addNotification } = useNotification()
    return async (serverId: number, serverName: string) => {
        try {
            const result = await drainServer(serverId)
            if (result.will_be_honoured === false) {
                addNotification(
                    `${serverName} was asked to drain, but it is not reporting its roster — ` +
                        'an older build will not act on this. Restart it instead.',
                    'error'
                )
            } else {
                addNotification(
                    `${serverName} is draining. It will stop taking joins and exit once ` + 'the players on it finish.',
                    'success'
                )
            }
            return true
        } catch (err: any) {
            addNotification(err.message, 'error')
            return false
        }
    }
}

export const DrainConfirm = ({
    serverId,
    serverName,
    online,
    reporting,
    onDone,
    onCancel,
}: {
    serverId: number
    serverName: string
    online: number
    // A server that does not report a roster is on a build that cannot act on
    // the instruction. Saying so up front is the difference between "nothing
    // happened" and "you were told nothing would happen".
    reporting: boolean
    onDone?: () => void
    onCancel: () => void
}) => {
    const [busy, setBusy] = useState(false)
    const drain = useDrain()

    const run = async () => {
        setBusy(true)
        try {
            if (await drain(serverId, serverName)) onDone?.()
        } finally {
            setBusy(false)
            onCancel()
        }
    }

    return (
        <div className='w-full min-w-0 whitespace-normal'>
            <ConfirmBar
                message={`Drain ${serverName}?`}
                detail={
                    (reporting
                        ? ''
                        : 'This server is not reporting its roster, so it is on a build that ' +
                          'cannot act on a drain — nothing will happen. Restart it instead. ') +
                    'Draining stops new joins immediately and removes the server from the ' +
                    'lobby. ' +
                    (online
                        ? `The ${online} player${online === 1 ? '' : 's'} on it now can finish; `
                        : 'Nobody is on it, so it exits shortly; ') +
                    'anyone still connected when the window runs out is disconnected. ' +
                    'No session is closed by hand and no earnings are affected — unlike a ' +
                    'restart, which ends every match immediately and needs the players refunded.'
                }
                confirmLabel='Drain'
                onConfirm={run}
                onCancel={onCancel}
                busy={busy}
            />
        </div>
    )
}
